import { atom, useAtomValue, useSetAtom, useStore } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { useEffect } from 'react'
import type { ReviewsNodesFragment } from '~/features/guide/reviewsNode.frag'
import { type FragmentType, graphql } from '~/lib/graphql'
import type {
  GetReviewedMealPeriodsQuery,
  GetReviewsQuery,
  GetReviewsQueryVariables,
  MealPeriod,
} from '~/lib/graphql/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { useInitAtom } from '~/lib/jotai'

const DEBUG_LABEL = 'reviews'
export const PAGE_SIZE = 20

export const GetReviewedMealPeriods = graphql(/* GraphQL */ `
  query GetReviewedMealPeriods($restaurantId: RestaurantId!) {
    restaurant(restaurantId: $restaurantId) {
      reviewedMealPeriods
    }
  }
`)

export function reviewsQueryKey(variables?: GetReviewsQueryVariables) {
  return ['reviews', variables] as const
}

const page$ = atom<number>(0)
page$.debugLabel = `${DEBUG_LABEL}-page$`

export function usePage() {
  return useAtomValue(page$)
}

const setPage$ = atom(null, (_get, set, v: number) => {
  set(page$, v)
})
setPage$.debugLabel = `${DEBUG_LABEL}-setPage`

export function useSetPage() {
  return useSetAtom(setPage$)
}

const mealPeriod$ = atom<MealPeriod | undefined>(undefined)
mealPeriod$.debugLabel = `${DEBUG_LABEL}-mealPeriod$`

const setMealPeriod$ = atom(null, (_get, set, v: MealPeriod) => {
  set(mealPeriod$, v === 'ALL' ? undefined : v)
  // 時間帯を変更した時はページをリセットする
  set(page$, 1)
})
setMealPeriod$.debugLabel = `${DEBUG_LABEL}-setMealPeriod`

export function useSetMealPeriod() {
  return useSetAtom(setMealPeriod$)
}

const restaurantId$ = atom<RestaurantId | undefined>(undefined)
restaurantId$.debugLabel = `${DEBUG_LABEL}-restaurantId$`

export function useInitVariables({
  mealPeriod,
  restaurantId,
  page,
}: { mealPeriod?: MealPeriod; restaurantId: RestaurantId; page: number }) {
  useInitAtom(restaurantId$, restaurantId)
  useInitAtom(mealPeriod$, mealPeriod)
  useInitAtom(page$, page)
}

const variables$ = atom<GetReviewsQueryVariables | undefined>((get) => {
  const restaurantId = get(restaurantId$)
  if (restaurantId === undefined) {
    return undefined
  }
  const mealPeriod = get(mealPeriod$)
  const page = get(page$)
  return {
    restaurantId,
    first: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    mealPeriod: mealPeriod ?? null,
  }
})
variables$.debugLabel = `${DEBUG_LABEL}-variables$`

export function useListenReviewCriteriaChange(
  onChange: (page: number, mealPeriod?: MealPeriod) => void
) {
  const store = useStore()
  useEffect(() => {
    return store.sub(variables$, () => {
      const mealPeriod = store.get(mealPeriod$)
      const page = store.get(page$)
      onChange(page, mealPeriod)
    })
  }, [store, onChange])
}

const selectedMealPeriod$ = atom<MealPeriod>((get) => {
  return get(mealPeriod$) ?? 'ALL'
})
selectedMealPeriod$.debugLabel = `${DEBUG_LABEL}-selectedMealPeriod$`

export function useSelectedMealPeriod() {
  return useAtomValue(selectedMealPeriod$)
}

const reviewsQuery$ = atomWithQuery((get) => ({
  queryKey: reviewsQueryKey(get(variables$)),
  queryFn: async ({ queryKey: [_, variables] }): Promise<GetReviewsQuery> => {
    const input = variables as GetReviewsQueryVariables
    const qs = new URLSearchParams(
      Object.entries({
        ...input,
      })
        .filter(([_k, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, String(v)])
    )
    const res = await fetch(`/api/v2/reviews?${qs}`)
    return res.json()
  },
  enabled: get(variables$) !== undefined,
  staleTime: 5 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
}))
reviewsQuery$.debugLabel = `${DEBUG_LABEL}-reviewsQuery$`

const reviews$ = atom<FragmentType<typeof ReviewsNodesFragment>[]>((get) => {
  return get(reviewsQuery$).data?.restaurant?.reviews2.edges.map((edge) => edge.node) ?? []
})
reviews$.debugLabel = `${DEBUG_LABEL}-reviews`

export function useReviews() {
  return useAtomValue(reviews$)
}

const totalCount$ = atom<number>((get) => {
  return get(reviewsQuery$).data?.restaurant?.reviews2.totalCount ?? 0
})
totalCount$.debugLabel = `${DEBUG_LABEL}-totalCount$`

export function useTotalCount() {
  return useAtomValue(totalCount$)
}

export function mealPeriodsQueryKey(restaurantId?: RestaurantId) {
  return ['reviewedMealPeriods', restaurantId] as const
}

const mealPeriodsQuery$ = atomWithQuery((get) => {
  return {
    queryKey: mealPeriodsQueryKey(get(restaurantId$)),
    queryFn: async ({ queryKey: [_, restaurantId] }): Promise<GetReviewedMealPeriodsQuery> => {
      const res = await fetch(`/api/v2/${restaurantId}/reviewed-meal-periods`)
      return res.json()
    },
    enabled: get(restaurantId$) !== undefined,
  }
})
mealPeriodsQuery$.debugLabel = `${DEBUG_LABEL}-mealPeriodsQuery$`

const sortedMealPeriods = ['ALL', 'DINNER', 'LUNCH', 'TEATIME', 'BREAKFAST'] satisfies MealPeriod[]
const mealPeriods$ = atom<MealPeriod[]>((get) => {
  const result = (get(mealPeriodsQuery$).data?.restaurant?.reviewedMealPeriods ?? []).concat('ALL')
  return sortedMealPeriods.filter((m) => result.includes(m))
})
mealPeriods$.debugLabel = `${DEBUG_LABEL}-mealPeriods$`

export function useMealPeriods() {
  return useAtomValue(mealPeriods$)
}
